import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin, Alert, Button, Tag, Typography, Empty } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { presencasService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Text, Title } = Typography;

// Consulta rápida, somente leitura, da chamada de um encontro já
// materializado -- reutiliza GET /api/presencas/encontros/{id}/chamada
// (presencasService.obterChamada, existente desde a Fase D). Nenhuma
// chamada PUT acontece neste componente; nenhuma edição é possível pelo
// modal. Escolhido Modal (não Drawer): é uma consulta pontual "olhar e
// fechar", sem necessidade de navegação -- a lista de alunos usa rolagem
// interna própria (maxHeight) para continuar utilizável mesmo em turmas
// grandes, sem precisar do espaço extra de um Drawer.
const STATUS_ALUNO_INFO = {
  PRESENTE: { label: 'Presente', color: 'green' },
  AUSENTE: { label: 'Ausente', color: 'red' },
  JUSTIFICADA: { label: 'Justificada', color: 'gold' },
  NAO_INFORMADA: { label: 'Não informada', color: 'default' },
};

const SITUACAO_ENCONTRO_INFO = {
  PREVISTA: { label: 'Pendente', color: 'gold' },
  REALIZADA: { label: 'Realizada', color: 'green' },
  CANCELADA: { label: 'Cancelada', color: 'default' },
};

const formatarHora = (valor) => (valor ? valor.slice(0, 5) : '--:--');

const formatarData = (valor) => {
  if (!valor) return '';
  const [ano, mes, dia] = valor.split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : valor;
};

// `linha`: dados da linha da tabela clicada (nome_turma/nome_unidade/
// nome_modalidade/nome_equipe) -- ChamadaAdminResponse (GET .../chamada)
// não traz esses nomes (só turma_id/equipe_id, ver
// app/schemas/presencas_admin.py), mesma limitação já documentada e
// resolvida na Fase D via dados de apresentação da listagem. `linha`
// nunca é fonte de verdade de autorização/regra, só rótulo.
const VisualizarChamadaModal = ({ open, encontroId, linha, onClose }) => {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [chamada, setChamada] = useState(null);
  // Guarda contra resposta tardia de uma requisição já obsoleta (modal
  // fechado/trocado antes do GET responder, ou clique duplo em "Tentar
  // novamente") -- nunca aplica setState de uma chamada que não é mais a
  // mais recente, nunca deixa erro "vazar" para a tela depois de fechado.
  const requisicaoAtualRef = useRef(0);

  const carregar = useCallback(() => {
    if (!encontroId) return;
    const idDestaRequisicao = requisicaoAtualRef.current + 1;
    requisicaoAtualRef.current = idDestaRequisicao;

    setChamada(null);
    setErro(null);
    setCarregando(true);
    presencasService.obterChamada(encontroId)
      .then((response) => {
        if (requisicaoAtualRef.current !== idDestaRequisicao) return;
        setChamada(response.data);
      })
      .catch((error) => {
        if (requisicaoAtualRef.current !== idDestaRequisicao) return;
        const { status, message } = buildApiErrorMessage(error, 'Não foi possível carregar a chamada.');
        setErro({ status, message });
      })
      .finally(() => {
        if (requisicaoAtualRef.current !== idDestaRequisicao) return;
        setCarregando(false);
      });
  }, [encontroId]);

  useEffect(() => {
    if (!open || !encontroId) return;
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, encontroId]);

  const situacaoInfo = chamada
    ? SITUACAO_ENCONTRO_INFO[chamada.status_encontro] || { label: chamada.status_encontro, color: 'default' }
    : null;

  return (
    <Modal
      title="Visualizar chamada"
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Fechar</Button>}
      width={640}
      destroyOnClose
    >
      {carregando && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spin size="large" tip="Carregando chamada..." />
        </div>
      )}

      {!carregando && erro && (
        <Alert
          type={erro.status === 404 ? 'warning' : 'error'}
          showIcon
          message="Não foi possível carregar a chamada"
          description={erro.message}
          action={
            erro.status !== 404 ? (
              <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
                Tentar novamente
              </Button>
            ) : undefined
          }
        />
      )}

      {!carregando && !erro && chamada && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Title level={5} style={{ marginBottom: 4 }}>
              {linha?.nome_turma || `Turma #${chamada.turma_id}`}
            </Title>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '4px 16px' }}>
              <Text><Text type="secondary">Data: </Text>{formatarData(chamada.data)}</Text>
              <Text><Text type="secondary">Horário: </Text>{formatarHora(chamada.hora_inicio)}–{formatarHora(chamada.hora_fim)}</Text>
              <Text><Text type="secondary">Unidade: </Text>{linha?.nome_unidade || '—'}</Text>
              <Text><Text type="secondary">Modalidade: </Text>{linha?.nome_modalidade || '—'}</Text>
              <Text><Text type="secondary">Professor: </Text>{linha?.nome_equipe || `Equipe #${chamada.equipe_id}`}</Text>
              <div><Tag color={situacaoInfo.color}>{situacaoInfo.label}</Tag></div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <Text>{chamada.total_presentes} Presente{chamada.total_presentes === 1 ? '' : 's'}</Text>
            <Text>{chamada.total_ausentes} Ausente{chamada.total_ausentes === 1 ? '' : 's'}</Text>
            <Text>{chamada.total_justificadas} Justificada{chamada.total_justificadas === 1 ? '' : 's'}</Text>
            {chamada.total_nao_informadas > 0 && (
              <Text>{chamada.total_nao_informadas} Não informada{chamada.total_nao_informadas === 1 ? '' : 's'}</Text>
            )}
          </div>

          {chamada.alunos.length === 0 ? (
            <Empty description="Nenhum aluno elegível para esta aula." />
          ) : (
            // Ordem preservada exatamente como veio de montar_chamada() --
            // nenhum sort() aplicado aqui.
            <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {chamada.alunos.map((aluno) => {
                const info = STATUS_ALUNO_INFO[aluno.status_frequencia] || { label: aluno.status_frequencia, color: 'default' };
                return (
                  <div
                    key={aluno.matricula_id}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                      gap: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div>
                      <Text strong>{aluno.nome_completo}</Text>
                      {aluno.observacao && (
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>{aluno.observacao}</Text>
                        </div>
                      )}
                    </div>
                    <Tag color={info.color}>{info.label}</Tag>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default VisualizarChamadaModal;
